import React from "react";
import type { CSSProperties } from "react";
import { formatPhoneNumberForDisplay } from "@utils/core";
import {
  TEMPLATE_LINE_HEIGHT_BODY,
  TEMPLATE_SPACING,
  TEMPLATE_TYPE_SCALE,
} from "@openings/template-components";

/* ── Booking-token CSS variables for uniform styling ── */

const BK_TEXT = "var(--openings-color-text, #1a1a1a)";
const BK_TEXT_SECONDARY = "var(--openings-color-text-secondary, #666)";
const BK_WEIGHT_SEMIBOLD = "var(--openings-booking-font-weight-semibold, 600)";
const DIVIDER_COLOR = "var(--openings-booking-skin-divider, #f0f0f0)";

export type BookingInfoSchedule = {
  id?: string | null;
  title?: string | null;
  name?: string | null;
  address?: string | null;
  parking?: { link?: string; description?: string } | null;
  phoneNumber?: string | null;
  instagramHandle?: string | null;
};

/** @deprecated Use {@link BookingInfoSchedule} instead. */
export type BookingInfoLocation = BookingInfoSchedule;

export type BookingInfoService = {
  id: string;
  title: string;
  description?: string | null;
  price?: number | null;
  duration?: number | null;
};

export type BookingInfoMember = {
  id: string;
  name: string;
  photo?: string | null;
};

/** The business blog, when it is on and has a live post. */
export type BookingInfoBlog = {
  /** Newest live post title, shown as the row value. */
  latestTitle: string;
  /** Defaults to `/blog`; the dev harness keeps navigation inside `/dev`. */
  href?: string;
};

export type BookingInfoPanelProps = {
  name?: string;
  /** @deprecated Use `schedules` instead. */
  locations?: BookingInfoSchedule[];
  schedules?: BookingInfoSchedule[];
  services?: BookingInfoService[];
  /** @deprecated Social links are no longer displayed in the info panel. */
  socialLinks?: unknown;
  members?: BookingInfoMember[];
  phoneNumber?: string | null;
  supportEmail?: string | null;
  instagramHandle?: string | null;
  blog?: BookingInfoBlog | null;
};

// ---------------------------------------------------------------------------
// Inline SVG icons (avoid @ant-design/icons for SSR stability)
// ---------------------------------------------------------------------------

const PhoneIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M877.1 238.7L770.6 132.3c-13-13-30.4-20.3-48.8-20.3s-35.8 7.2-48.8 20.3L558.3 246.8c-13 13-20.3 30.5-20.3 48.9 0 18.5 7.2 35.8 20.3 48.9l89.6 89.7a405.46 405.46 0 01-86.4 127.3c-36.7 36.9-79.6 66-127.2 86.3l-89.6-89.7c-13-13-30.4-20.3-48.8-20.3a68.2 68.2 0 00-48.8 20.3L132.3 672.7c-13 13-20.3 30.5-20.3 48.9 0 18.5 7.2 35.8 20.3 48.9l106.4 106.3c22.2 22.2 52.8 34.9 84.2 34.9 6.5 0 12.8-.5 19.2-1.6 132.4-21.8 263.7-92.3 369.4-198.1C827 594.7 897.7 463.6 919.2 330.7c11.3-65-9.6-130.7-42.1-92z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 110.8V792H136V270.8l-27.6-21.5 39.3-50.5 42.8 33.3h643l42.8-33.3 39.3 50.5-27.6 21.5zM833.6 232L512 482 190.4 232l-42.8-33.3-39.3 50.5 27.6 21.5 341.6 265.6a55.99 55.99 0 0068.7 0L888 270.8l27.6-21.5-39.3-50.5-42.7 33.2z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 448 512" width="1em" height="1em" fill="currentColor">
    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M512 327c-29.9 0-58 11.6-79.2 32.8A111.6 111.6 0 00400 439c0 29.9 11.7 58 32.8 79.2A111.6 111.6 0 00512 551c29.9 0 58-11.7 79.2-32.8C612.4 497 624 468.9 624 439s-11.6-58-32.8-79.2S541.9 327 512 327zm342.6-37.9a362.49 362.49 0 00-79.9-115.7 370.83 370.83 0 00-118.2-77.8C610.7 76.6 562.1 67 512 67c-50.1 0-98.7 9.6-144.5 28.5-44.3 18.3-84 44.5-118.2 77.8A363.6 363.6 0 00169.4 289c-19.5 45-29.4 92.8-29.4 142 0 70.6 16.9 140.9 50.1 208.7 26.7 54.5 64 107.6 111 158.1 80.3 86.2 164.5 138.9 188.4 153a43.9 43.9 0 0022.4 6.1c7.8 0 15.5-2 22.4-6.1 23.9-14.1 108.1-66.8 188.4-153 47-50.4 84.3-103.6 111-158.1C893.1 572.9 910 502.6 910 432c0-49.2-9.9-97-29.4-142zM512 615c-97.2 0-176-78.8-176-176s78.8-176 176-176 176 78.8 176 176-78.8 176-176 176z" />
  </svg>
);

const ArticleIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M832 64H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V96c0-17.7-14.3-32-32-32zm-40 824H232V136h560v752zM320 340h384c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8H320c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8zm0 144h384c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8H320c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8zm0 144h200c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8H320c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8z" />
  </svg>
);

const ParkingIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path d="M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Row component — each info section row
// ---------------------------------------------------------------------------

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: `${TEMPLATE_SPACING.md} 0`,
  borderBottom: `1px solid ${DIVIDER_COLOR}`,
};

const rowLabelStyle: CSSProperties = {
  fontWeight: BK_WEIGHT_SEMIBOLD,
  fontSize: TEMPLATE_TYPE_SCALE.body,
  lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
  color: BK_TEXT,
  margin: 0,
};

const rowValueStyle: CSSProperties = {
  fontSize: TEMPLATE_TYPE_SCALE.small,
  lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
  color: BK_TEXT_SECONDARY,
  margin: `4px 0 0`,
};

const iconBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: "var(--openings-booking-muted-chip, #f5f5f5)",
  color: BK_TEXT,
  fontSize: 18,
  border: "none",
  cursor: "pointer",
  flexShrink: 0,
  textDecoration: "none",
};

const rowLinkStyle: CSSProperties = {
  color: "inherit",
  textDecoration: "none",
};

const singleLineValueStyle: CSSProperties = {
  ...rowValueStyle,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Blog row: the whole row links to the blog, like a list cell. */
function BlogRow({ blog }: { blog: BookingInfoBlog }) {
  return (
    <a href={blog.href ?? "/blog"} style={rowLinkStyle} data-booking-info-blog>
      <div style={rowStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={rowLabelStyle}>Blog</p>
          <p style={singleLineValueStyle}>{blog.latestTitle}</p>
        </div>
        <span style={iconBtnStyle} aria-hidden="true">
          <ArticleIcon />
        </span>
      </div>
    </a>
  );
}

function InfoRow({
  label,
  value,
  actions,
}: {
  label: string;
  value?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div style={rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={rowLabelStyle}>{label}</p>
        {value && <p style={rowValueStyle}>{value}</p>}
      </div>
      {actions && (
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>{actions}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function BookingInfoPanel({
  name: _name,
  locations = [],
  schedules,
  services: _services = [],
  members: _members = [],
  phoneNumber,
  supportEmail,
  instagramHandle,
  blog,
}: BookingInfoPanelProps) {
  const scheduleItems = schedules ?? locations;

  const physicalLocations = scheduleItems.filter((s) =>
    Boolean(s.address?.trim?.()),
  );
  // When exactly 1 schedule is passed (selected location), show its full info
  const isSingleSelected = scheduleItems.length === 1;
  const hasLocations = physicalLocations.length > 0;
  const hasPhone = Boolean(phoneNumber?.trim?.());
  const hasEmail = Boolean(supportEmail?.trim?.());
  const hasInstagram = Boolean(instagramHandle?.trim?.());
  const hasBlog = Boolean(blog?.latestTitle?.trim());

  const hasAnything =
    isSingleSelected ||
    hasLocations ||
    hasPhone ||
    hasEmail ||
    hasInstagram ||
    hasBlog;

  if (!hasAnything) return null;

  // Resolve the display name for each schedule
  const displayName = (s: BookingInfoSchedule) =>
    s.title || s.name || undefined;

  // Extract instagram username from URL or handle
  const extractInstagramUsername = (handle?: string | null) => {
    if (!handle) return null;
    const trimmed = handle.trim();
    if (!trimmed) return null;
    // Strip URL prefix if present
    const match = trimmed.match(
      /(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?#]+)/i,
    );
    if (match) return match[1];
    // Already a username (possibly with @)
    return trimmed.replace(/^@/, "");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* ── Schedule stage: single selected location — full details ── */}
      {isSingleSelected &&
        (() => {
          const loc = scheduleItems[0];
          const locName = displayName(loc);
          const locAddress = loc.address?.trim();

          // Fallback: schedule contact → business contact
          const effectivePhone =
            loc.phoneNumber?.trim() || phoneNumber?.trim() || null;
          const effectiveEmail = supportEmail?.trim() || null;
          const scheduleIg = extractInstagramUsername(loc.instagramHandle);
          const businessIg = extractInstagramUsername(instagramHandle);
          const effectiveIg = scheduleIg || businessIg;

          return (
            <>
              {/* Location */}
              <InfoRow
                label={locName || "Location"}
                value={locAddress}
                actions={
                  locAddress ? (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(locAddress)}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={iconBtnStyle}
                      aria-label="Open in maps"
                    >
                      <LocationIcon />
                    </a>
                  ) : undefined
                }
              />

              {/* Parking */}
              {loc.parking?.description?.trim() && (
                <InfoRow
                  label="Parking"
                  value={loc.parking.description.trim()}
                  actions={
                    loc.parking?.link?.trim() ? (
                      <a
                        href={loc.parking.link.trim()}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={iconBtnStyle}
                        aria-label="Parking info"
                      >
                        <ParkingIcon />
                      </a>
                    ) : undefined
                  }
                />
              )}

              {/* Phone */}
              {effectivePhone && (
                <InfoRow
                  label="Phone"
                  value={formatPhoneNumberForDisplay(effectivePhone)}
                  actions={
                    <a
                      href={`tel:${effectivePhone}`}
                      style={iconBtnStyle}
                      aria-label="Call"
                    >
                      <PhoneIcon />
                    </a>
                  }
                />
              )}

              {/* Email */}
              {effectiveEmail && (
                <InfoRow
                  label="Email"
                  value={effectiveEmail}
                  actions={
                    <a
                      href={`mailto:${effectiveEmail}`}
                      style={iconBtnStyle}
                      aria-label="Send email"
                    >
                      <MailIcon />
                    </a>
                  }
                />
              )}

              {/* Follow */}
              {effectiveIg && (
                <InfoRow
                  label="Follow"
                  value={`@${effectiveIg}`}
                  actions={
                    <a
                      href={`https://instagram.com/${effectiveIg}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={iconBtnStyle}
                      aria-label="Instagram"
                    >
                      <InstagramIcon />
                    </a>
                  }
                />
              )}
            </>
          );
        })()}

      {/* ── Locations stage: list of locations (no parking / contact) ── */}
      {!isSingleSelected && hasLocations && physicalLocations.length === 1 && (
        <InfoRow
          label={displayName(physicalLocations[0]) || "Location"}
          value={physicalLocations[0].address?.trim()}
          actions={
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(physicalLocations[0].address?.trim() ?? "")}`}
              target="_blank"
              rel="noreferrer noopener"
              style={iconBtnStyle}
              aria-label="Open in maps"
            >
              <LocationIcon />
            </a>
          }
        />
      )}
      {!isSingleSelected && hasLocations && physicalLocations.length > 1 && (
        <div
          style={{
            padding: `${TEMPLATE_SPACING.md} 0`,
            borderBottom: `1px solid ${DIVIDER_COLOR}`,
          }}
        >
          <p style={rowLabelStyle}>Locations</p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginTop: 8,
            }}
          >
            {physicalLocations.map((schedule, i) => {
              const locName = displayName(schedule);
              return (
                <div
                  key={schedule.id ?? `loc-${i}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {locName && (
                      <p
                        style={{
                          ...rowLabelStyle,
                          fontSize: TEMPLATE_TYPE_SCALE.small,
                          margin: 0,
                        }}
                      >
                        {locName}
                      </p>
                    )}
                    <p style={{ ...rowValueStyle, margin: 0 }}>
                      {schedule.address?.trim()}
                    </p>
                  </div>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(schedule.address?.trim() ?? "")}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    style={{
                      ...iconBtnStyle,
                      // Skins raise it to the 40px touch-target floor; the
                      // flat presets keep 32px. The glyph stays small.
                      width: "var(--openings-booking-skin-touch-target, 32px)",
                      height: "var(--openings-booking-skin-touch-target, 32px)",
                      fontSize: 14,
                    }}
                    aria-label="Open in maps"
                  >
                    <LocationIcon />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Business-level contact (locations stage only) */}
      {!isSingleSelected && hasPhone && (
        <InfoRow
          label="Phone"
          value={formatPhoneNumberForDisplay(phoneNumber)}
          actions={
            <a
              href={`tel:${phoneNumber}`}
              style={iconBtnStyle}
              aria-label="Call"
            >
              <PhoneIcon />
            </a>
          }
        />
      )}
      {!isSingleSelected && hasEmail && (
        <InfoRow
          label="Email"
          value={supportEmail}
          actions={
            <a
              href={`mailto:${supportEmail}`}
              style={iconBtnStyle}
              aria-label="Send email"
            >
              <MailIcon />
            </a>
          }
        />
      )}
      {!isSingleSelected &&
        hasInstagram &&
        (() => {
          const ig = extractInstagramUsername(instagramHandle);
          if (!ig) return null;
          return (
            <InfoRow
              label="Follow"
              value={`@${ig}`}
              actions={
                <a
                  href={`https://instagram.com/${ig}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={iconBtnStyle}
                  aria-label="Instagram"
                >
                  <InstagramIcon />
                </a>
              }
            />
          );
        })()}

      {/* Blog (both stages) */}
      {hasBlog && blog && <BlogRow blog={blog} />}
    </div>
  );
}
